import { FirebaseError } from 'firebase/app';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
  type DocumentData,
  type Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';

const INVITES_COLLECTION = 'invites';
const ORGS_COLLECTION = 'orgs';
const APP_USERS_COLLECTION = 'appUsers';

const OPERATOR_PERMISSIONS = [
  'customers.read', 'customers.write', 'cropStatus.read',
];

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Invite {
  token: string;
  orgId: string;
  orgName: string;
  email: string;
  createdBy: string;
  createdAt: Timestamp;
  expiresAt: Timestamp;
  isRevoked: boolean;
  acceptedAt: Timestamp | null;
  acceptedBy: string | null;
}

export interface InviteServiceError {
  code: string;
  message: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function docToInvite(token: string, data: DocumentData, orgName: string = ''): Invite {
  return {
    token,
    orgId: data.orgId as string,
    orgName,
    email: data.email as string,
    createdBy: data.createdBy as string,
    createdAt: data.createdAt as Timestamp,
    expiresAt: data.expiresAt as Timestamp,
    isRevoked: (data.isRevoked as boolean) ?? false,
    acceptedAt: data.acceptedAt ?? null,
    acceptedBy: data.acceptedBy ?? null,
  };
}

function toServiceError(error: unknown): InviteServiceError {
  if (error instanceof FirebaseError) {
    return { code: error.code, message: error.message };
  }
  return { code: 'unknown', message: 'An unexpected error occurred' };
}

// ─── Invite functions ─────────────────────────────────────────────────────────

/**
 * Creates an invite for a user to join an org.
 * Returns the random token string.
 */
export async function createInvite(orgId: string, email: string, creatorUid: string): Promise<string> {
  try {
    const inviteRef = doc(collection(db, INVITES_COLLECTION));
    const token = inviteRef.id;
    const now = serverTimestamp();
    const expiresAt = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000);

    const batch = writeBatch(db);
    batch.set(inviteRef, {
      orgId,
      email,
      createdBy: creatorUid,
      createdAt: now,
      expiresAt,
      isRevoked: false,
      acceptedAt: null,
      acceptedBy: null,
    });
    await batch.commit();

    return token;
  } catch (error) {
    throw toServiceError(error);
  }
}

/** Reads an invite by token. Returns null if not found. */
export async function getInvite(token: string): Promise<Invite | null> {
  try {
    const ref = doc(db, INVITES_COLLECTION, token);
    const snap = await getDoc(ref);

    if (!snap.exists()) {
      return null;
    }

    const inviteData = snap.data();
    const orgId = inviteData.orgId as string;

    // Fetch org name
    let orgName = '';
    try {
      const orgSnap = await getDoc(doc(db, ORGS_COLLECTION, orgId));
      if (orgSnap.exists()) {
        orgName = (orgSnap.data().name as string) ?? '';
      }
    } catch {
      // If org fetch fails, continue with empty name
    }

    return docToInvite(snap.id, inviteData, orgName);
  } catch (error) {
    throw toServiceError(error);
  }
}

/**
 * Accepts an invite.
 * - Validates: not expired, not revoked
 * - Creates member doc in org + membership entry in appUsers
 * - Updates invite with acceptedAt + acceptedBy
 */
export async function acceptInvite(
  token: string,
  invite: Invite,
  userUid: string,
  userEmail: string,
): Promise<void> {
  try {
    // Validate
    if (invite.isRevoked) {
      throw new Error('This invite has been revoked.');
    }
    if (invite.acceptedAt) {
      throw new Error('This invite has already been accepted.');
    }
    if (new Date() > invite.expiresAt.toDate()) {
      throw new Error('This invite has expired.');
    }

    const batch = writeBatch(db);
    const now = serverTimestamp();

    // Create member doc in org
    batch.set(
      doc(db, ORGS_COLLECTION, invite.orgId, 'members', userUid),
      {
        role: 'operator',
        permissions: OPERATOR_PERMISSIONS,
        isDisabled: false,
        displayName: '', // Will be populated from appUser on read
        email: userEmail,
        createdAt: now,
        lastSignIn: now,
      },
    );

    // Create membership entry
    // Note: orgName and orgSlug will be populated by the OrgContext when the user
    // navigates to the org. We don't fetch the org doc here because the read rule
    // requires the user to already be a member.
    batch.set(
      doc(db, APP_USERS_COLLECTION, userUid, 'memberships', invite.orgId),
      {
        orgId: invite.orgId,
        orgName: '',
        orgSlug: '',
        joinedAt: now,
      },
    );

    // Mark invite as accepted
    batch.update(
      doc(db, INVITES_COLLECTION, token),
      {
        acceptedAt: now,
        acceptedBy: userUid,
      },
    );

    await batch.commit();

    // Fetch org name and slug and update membership entry
    // We can now read the org doc because the user is a member (from the batch write above)
    const orgSnap = await getDoc(doc(db, ORGS_COLLECTION, invite.orgId));
    if (orgSnap.exists()) {
      const orgData = orgSnap.data();
      const orgName = (orgData.name as string) ?? '';
      const orgSlug = (orgData.slug as string) ?? '';
      await updateDoc(
        doc(db, APP_USERS_COLLECTION, userUid, 'memberships', invite.orgId),
        { orgName, orgSlug },
      );
    }
  } catch (error) {
    throw toServiceError(error);
  }
}

/** Revokes an invite. Used for both admin revocation and invitee decline. */
export async function revokeInvite(token: string): Promise<void> {
  try {
    await updateDoc(doc(db, INVITES_COLLECTION, token), {
      isRevoked: true,
    });
  } catch (error) {
    throw toServiceError(error);
  }
}

/**
 * Gets all pending (non-revoked, non-accepted, non-expired) invites for an org.
 * Used by super admins to review and revoke invites.
 */
export async function getOrgPendingInvites(orgId: string): Promise<Invite[]> {
  try {
    // Fetch org name once
    let orgName = '';
    const orgSnap = await getDoc(doc(db, ORGS_COLLECTION, orgId));
    if (orgSnap.exists()) {
      orgName = (orgSnap.data().name as string) ?? '';
    }

    const q = query(
      collection(db, INVITES_COLLECTION),
      where('orgId', '==', orgId),
      where('isRevoked', '==', false),
      where('acceptedAt', '==', null),
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => docToInvite(d.id, d.data(), orgName));
  } catch (error) {
    throw toServiceError(error);
  }
}
